// Utility functions for campaign wizard state persistence
// Based on peer review recommendations

/**
 * Generate namespaced storage keys for a specific campaign
 * This prevents collisions between multiple draft campaigns
 */
export const getWizardStorageKey = (campaignId: string) => {
  return `campaign_wizard_${campaignId}_step`;
};

/**
 * Read the current step from sessionStorage with validation
 * @param campaignId - The campaign UUID
 * @param maxSteps - Maximum number of steps in the wizard
 * @returns The validated step number (defaults to 1)
 */
export function readWizardStep(campaignId: string | undefined, maxSteps: number): number {
  if (!campaignId) {
    console.log('🔍 readWizardStep: No campaignId provided, returning step 1');
    return 1;
  }
  
  try {
    const key = getWizardStorageKey(campaignId);
    const storedValue = sessionStorage.getItem(key);
    
    console.log('🔍 readWizardStep:', {
      campaignId,
      key,
      storedValue,
      maxSteps
    });
    
    if (!storedValue) {
      console.log('🔍 readWizardStep: No stored value found, returning step 1');
      return 1;
    }
    
    const step = parseInt(storedValue, 10);
    
    // Validate and clamp the step number
    if (!Number.isFinite(step)) {
      console.log('🔍 readWizardStep: Invalid step value, returning step 1');
      return 1;
    }
    
    const finalStep = Math.min(Math.max(step, 1), maxSteps);
    console.log('🔍 readWizardStep: Returning step', finalStep);
    return finalStep;
  } catch (error) {
    console.warn('Error reading wizard step from sessionStorage:', error);
    return 1;
  }
}

/**
 * Write the current step to sessionStorage
 * @param campaignId - The campaign UUID
 * @param step - The current step number
 */
export function writeWizardStep(campaignId: string | undefined, step: number): void {
  if (!campaignId) {
    console.log('📝 writeWizardStep: No campaignId provided, skipping');
    return;
  }
  
  try {
    const key = getWizardStorageKey(campaignId);
    sessionStorage.setItem(key, String(step));
    
    // Also store timestamp for potential cleanup later
    sessionStorage.setItem(`${key}_timestamp`, String(Date.now()));
    
    console.log('📝 writeWizardStep: Saved step', {
      campaignId,
      key,
      step,
      timestamp: Date.now()
    });
  } catch (error) {
    // Storage might be disabled or full
    console.warn('Error writing wizard step to sessionStorage:', error);
  }
}

/**
 * Clear the stored wizard step (on completion or cancel)
 * @param campaignId - The campaign UUID
 */
export function clearWizardStep(campaignId: string | undefined): void {
  if (!campaignId) return;
  
  try {
    const key = getWizardStorageKey(campaignId);
    sessionStorage.removeItem(key);
    sessionStorage.removeItem(`${key}_timestamp`);
  } catch (error) {
    console.warn('Error clearing wizard step from sessionStorage:', error);
  }
}

/**
 * Update the URL to include the campaign ID for persistence
 * @param campaignId - The campaign UUID
 */
export function updateWizardUrl(campaignId: string): void {
  try {
    const url = new URL(window.location.href);
    url.searchParams.set('edit', campaignId);
    // Remove step from URL if it exists (keep it in sessionStorage only)
    url.searchParams.delete('step');
    
    // Use replaceState to avoid polluting browser history
    window.history.replaceState(null, '', url.toString());
  } catch (error) {
    console.error('Error updating wizard URL:', error);
  }
}

/**
 * Clean up old wizard state entries (optional housekeeping)
 * Removes entries older than 24 hours
 */
export function cleanupOldWizardState(): void {
  try {
    const now = Date.now();
    const maxAge = 24 * 60 * 60 * 1000; // 24 hours
    
    // Iterate through sessionStorage keys
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key && key.startsWith('campaign_wizard_') && key.endsWith('_timestamp')) {
        const timestamp = parseInt(sessionStorage.getItem(key) || '0', 10);
        if (now - timestamp > maxAge) {
          // Remove old entries
          const stepKey = key.replace('_timestamp', '');
          sessionStorage.removeItem(stepKey);
          sessionStorage.removeItem(key);
        }
      }
    }
  } catch (error) {
    console.warn('Error cleaning up old wizard state:', error);
  }
}